import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ActionButton from "components/Buttons/ActionButton";
import EmailField from "components/EmailField";
import TEST_ID from "enums/TEST_ID";
import usePostParticipantSignup from "hooks/api/usePostParticipantSignup";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { getDataTestProp } from "utils/cypressTesting";
import isEmail from "utils/validators/isEmail";
import isName from "utils/validators/isName";

export default function JoinPage() {
  const { projectId = "", scriptId = "" } = useParams<{
    projectId: string;
    scriptId: string;
  }>();
  const { postParticipantSignup, isWriting } = usePostParticipantSignup();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState<string | undefined>();
  const [submitted, setSubmitted] = useState(false);

  const firstNameValid = isName(firstName);
  const lastNameValid = isName(lastName);
  const emailValid = email != undefined && isEmail(email);
  const canSubmit = firstNameValid && lastNameValid && emailValid && !isWriting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !email) return;
    try {
      await postParticipantSignup({
        email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        projectId,
        scriptId,
      });
      setSubmitted(true);
    } catch {
      // useApiWrite surfaces API errors via useNotifications
    }
  }

  if (submitted) {
    return (
      <Card
        sx={{ maxWidth: 480, width: "100%" }}
        {...getDataTestProp(TEST_ID.participantSignup.confirmation)}
      >
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Check your email
          </Typography>
          <Typography color="text.secondary">
            We sent a confirmation link to {email}. Click the link in that email
            to finish signing up. After you confirm, you will receive another
            email with your app login details.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 480, width: "100%" }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Join this study
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Enter your name and email. We will send you a link to confirm your
          email address.
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          {...getDataTestProp(TEST_ID.participantSignup.form)}
        >
          <Stack spacing={2}>
            <TextField
              {...getDataTestProp(TEST_ID.participantSignup.firstName)}
              label="First name"
              required
              fullWidth
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={firstName.length > 0 && !firstNameValid}
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
            <TextField
              {...getDataTestProp(TEST_ID.participantSignup.lastName)}
              label="Last name"
              required
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={lastName.length > 0 && !lastNameValid}
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
            <EmailField
              {...getDataTestProp(TEST_ID.participantSignup.email)}
              required
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <ActionButton
              {...getDataTestProp(TEST_ID.participantSignup.submit)}
              type="submit"
              disabled={!canSubmit}
            >
              {isWriting ? "Submitting…" : "Continue"}
            </ActionButton>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
